/**
 * Mapeamento entre `activeTab` (estado interno legado) e URLs públicas.
 *
 * Manter o `activeTab` permite refatoração incremental: a tela continua
 * funcionando com `setActiveTab` enquanto a URL é o "source of truth" externo.
 */

export type AppTab =
  | 'home'
  | 'explore'
  | 'favorites'
  | 'profile'
  | 'scanner'
  | 'admin'
  | 'ai'
  | 'partner'
  | 'indication';

export const PATHS: Record<AppTab, string> = {
  home: '/',
  explore: '/explorar',
  favorites: '/salvos',
  profile: '/perfil',
  scanner: '/scanner',
  admin: '/admin',
  ai: '/sixy',
  partner: '/parceiros/cadastrar',
  indication: '/indicar',
};

export const LOGIN_PATH = '/entrar';
export const PARTNER_JOIN_PATH = '/parceiros/cadastrar';
export const PRIVACY_PATH = '/privacidade';
export const TERMS_PATH = '/termos';

/** Converte `activeTab` para o pathname correspondente. */
export function tabToPath(tab: AppTab | string): string {
  if (typeof tab === 'string' && tab in PATHS) {
    return PATHS[tab as AppTab];
  }
  return '/';
}

/** Identifica o `activeTab` a partir de um pathname. */
export function pathToTab(pathname: string): AppTab {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/' || normalized === '') return 'home';
  if (normalized.startsWith('/explorar')) return 'explore';
  if (normalized.startsWith('/salvos')) return 'favorites';
  if (normalized.startsWith('/perfil')) return 'profile';
  if (normalized.startsWith('/scanner')) return 'scanner';
  if (normalized.startsWith('/admin')) return 'admin';
  if (normalized.startsWith('/sixy')) return 'ai';
  if (normalized.startsWith('/parceiros/cadastrar')) return 'partner';
  if (normalized.startsWith('/indicar')) return 'indication';
  return 'home';
}

/**
 * Lista de pathnames considerados válidos pelo app (usado pra detectar URLs
 * desconhecidas e mostrar a tela 404).
 */
export function isKnownPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/' || normalized === '') return true;
  const knownPrefixes = [
    '/explorar',
    '/salvos',
    '/perfil',
    '/scanner',
    '/admin',
    '/sixy',
    '/parceiros/cadastrar',
    '/indicar',
    '/entrar',
    '/privacidade',
    '/termos',
  ];
  if (knownPrefixes.some((p) => normalized === p || normalized.startsWith(p + '/'))) return true;
  // Deep link de parceiro: /parceiros/<id>
  if (getPartnerIdFromPath(normalized)) return true;
  // URL pública compartilhável: /p/<id>
  if (getPartnerIdFromPublicPath(normalized)) return true;
  return false;
}

/**
 * Extrai o id do parceiro de uma URL `/parceiros/<id>`.
 * Retorna null para `/parceiros/cadastrar` (rota reservada).
 */
export function getPartnerIdFromPath(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, '');
  const match = normalized.match(/^\/parceiros\/([^/]+)$/);
  if (!match) return null;
  const id = match[1];
  if (id === 'cadastrar') return null;
  return id;
}

/** Constrói o caminho do deep link de um parceiro a partir do id. */
export function partnerDetailPath(partnerId: string): string {
  return `/parceiros/${encodeURIComponent(partnerId)}`;
}

/**
 * URL pública (curta) compartilhável de um parceiro. Renderiza a página
 * de marketing — funciona logado e deslogado. Usada no share button do
 * PartnerModal e no Insta/WhatsApp do parceiro.
 */
export function partnerPublicPath(partnerId: string): string {
  return `/p/${encodeURIComponent(partnerId)}`;
}

/** Extrai o id de uma URL do tipo /p/<id>. Retorna null se não bater. */
export function getPartnerIdFromPublicPath(pathname: string): string | null {
  const m = pathname.match(/^\/p\/([^/?#]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}
